import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';

import { api, ErroApi } from './api';

/**
 * Assistente flutuante.
 *
 * Fica em todas as telas, sempre no mesmo canto. É o único lugar do produto que
 * responde tanto sobre **dados** ("quantas máquinas no Windows 10?") quanto
 * sobre **como as coisas funcionam** ("por que essa máquina está
 * indeterminada?") — e a diferença entre as duas é decidida no servidor.
 *
 * O conhecimento é embarcado, porque a instalação não tem internet. E o
 * assistente **diz que não sabe** quando não sabe: num produto que fala sobre o
 * parque de alguém, uma invenção plausível é pior que uma recusa.
 */

interface Fonte {
  readonly titulo: string;
  readonly area: string;
}

/** O que será feito, quando a pergunta foi uma ordem executável. */
interface Plano {
  readonly capacidade: string;
  readonly risco: 'BAIXO' | 'ALTO';
  readonly oQue: string;
  readonly alvo: string;
  readonly maquinas: number | null;
  readonly autorizacao: 'chat' | 'aprovacao' | 'previa';
  readonly ressalva: string | null;
}

interface Resposta {
  readonly tipo: 'conhecimento' | 'dados' | 'nao_sei' | 'plano';
  readonly texto: string;
  readonly fontes: readonly Fonte[];
  readonly dados?: {
    readonly descricao: string;
    readonly total: number;
    readonly grupos: readonly { readonly chave: string; readonly total: number }[];
  };
  readonly plano?: Plano;
  readonly leva: string | null;
  readonly perguntaResolvida: string;
  readonly herdouContexto: boolean;
  readonly contexto: string | null;
}

interface Fala {
  readonly de: 'pessoa' | 'assistente';
  readonly texto: string;
  readonly resposta?: Resposta;
  /**
   * 🔴 A pergunta que gerou esta resposta — é ELA que o botão de confirmar
   * reenvia, nunca o plano.
   *
   * O servidor refaz tudo a partir da frase. Mandar o plano de volta seria
   * deixar o navegador decidir em quantas máquinas executar, e quem controla o
   * navegador controla o JSON.
   */
  readonly perguntaOriginal?: string;
}

const SUGESTOES = [
  'Quantas máquinas ainda estão no Windows 10?',
  'Qual o software mais instalado no parque?',
  'Por que uma máquina fica "sem dados para decidir"?',
  'Como funciona a remediação?',
];

export function AssistenteFlutuante({
  aoPerderSessao,
  irPara,
}: {
  aoPerderSessao: () => void;
  irPara: (aba: string) => void;
}): ReactNode {
  const [aberto, setAberto] = useState(false);
  const [pergunta, setPergunta] = useState('');
  const [falas, setFalas] = useState<Fala[]>([]);
  const [pensando, setPensando] = useState(false);
  /**
   * Última pergunta **resolvida**, não a digitada.
   *
   * É o que permite encadear: "e no Windows 11?" vira a base de "e no 7?".
   * Quem decide o que avança é o servidor — ver `contextoSeguinte`.
   */
  const [anterior, setAnterior] = useState<string | null>(null);
  /** Índice da fala cuja execução está em curso. Impede clique duplo. */
  const [executando, setExecutando] = useState<number | null>(null);
  const fim = useRef<HTMLDivElement | null>(null);
  const campo = useRef<HTMLInputElement | null>(null);

  // Rola para a última fala. Sem isto, a resposta nasce fora da vista quando a
  // conversa passa da altura do painel.
  useEffect(() => {
    fim.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [falas, pensando]);

  const ultima = falas[falas.length - 1];

  async function enviar(texto: string): Promise<void> {
    const p = texto.trim();
    if (p === '' || pensando) return;

    setFalas((f) => [...f, { de: 'pessoa', texto: p }]);
    setPergunta('');
    setPensando(true);

    try {
      const r = await api.post<Resposta>('/api/assistente/perguntar', {
        pergunta: p,
        anterior,
      });
      // `contexto`, não `perguntaResolvida`: pergunta lateral de conhecimento
      // não pode apagar o assunto de dados que estava em curso.
      setAnterior(r.contexto);
      setFalas((f) => [
        ...f,
        { de: 'assistente', texto: r.texto, resposta: r, perguntaOriginal: p },
      ]);
    } catch (e: unknown) {
      if (e instanceof ErroApi && e.precisaLogin) {
        aoPerderSessao();
        return;
      }
      setFalas((f) => [
        ...f,
        {
          de: 'assistente',
          texto: `Não consegui responder agora: ${e instanceof Error ? e.message : String(e)}`,
        },
      ]);
    } finally {
      setPensando(false);
      // Devolve o cursor para o campo. É uma conversa: quem acabou de ler a
      // resposta quase sempre quer perguntar a próxima coisa.
      campo.current?.focus();
    }
  }

  /**
   * Confirmar e executar.
   *
   * ⚠️ Manda só a frase. O servidor remonta o plano inteiro antes de escrever
   * qualquer coisa — ver a nota em `/api/assistente/executar`.
   */
  async function confirmar(indice: number, perguntaOriginal: string): Promise<void> {
    if (executando !== null) return;
    setExecutando(indice);
    try {
      const r = await api.post<{
        kind: string;
        texto?: string;
        aceitas?: number;
        autorizadoPor?: string;
      }>('/api/assistente/executar', { pergunta: perguntaOriginal });
      const texto =
        r.kind === 'executado'
          ? `Pedido enviado para **${r.aceitas} máquina(s)** — autorizado por ${r.autorizadoPor ?? '—'}.\n\n` +
            '⚠️ "Pedido aceito" não é "deu certo": a máquina pode estar desligada. ' +
            'A verificação confere a evidência depois, na aba Execuções.'
          : (r.texto ?? 'Feito.');
      setFalas((f) => [...f, { de: 'assistente', texto }]);
    } catch (e: unknown) {
      if (e instanceof ErroApi && e.precisaLogin) {
        aoPerderSessao();
        return;
      }
      setFalas((f) => [
        ...f,
        { de: 'assistente', texto: `Não executei: ${e instanceof Error ? e.message : String(e)}` },
      ]);
    } finally {
      setExecutando(null);
    }
  }

  if (!aberto) {
    return (
      <button
        className="bolha-assistente"
        onClick={() => setAberto(true)}
        title="Perguntar ao assistente"
        aria-label="Abrir assistente"
      >
        ✦
      </button>
    );
  }

  return (
    <div className="assistente" role="dialog" aria-label="Assistente">
      <header>
        <div>
          <strong>Assistente</strong>
          <span>Sobre o seu parque e sobre o KACE</span>
        </div>
        <div>
          {falas.length > 0 && (
            <button
              className="icone"
              onClick={() => {
                setFalas([]);
                setAnterior(null);
              }}
              title="Começar uma conversa nova"
              aria-label="Limpar conversa"
            >
              ↺
            </button>
          )}
          <button className="icone" onClick={() => setAberto(false)} aria-label="Fechar assistente">
            ✕
          </button>
        </div>
      </header>

      <div className="conversa">
        {falas.length === 0 && (
          <>
            <p className="suave abertura">
              Pergunte em português. Eu respondo sobre o seu parque e sobre como a plataforma e o
              KACE funcionam.
            </p>
            <div className="sugestoes">
              {SUGESTOES.map((s) => (
                <button key={s} onClick={() => void enviar(s)}>
                  {s}
                </button>
              ))}
            </div>
          </>
        )}

        {falas.map((f, i) => (
          <div key={i} className={`fala de-${f.de}`}>
            <div className="balao">
              {/*
                Reescrita invisível é reescrita que ninguém consegue conferir.
                Quando a pergunta herdou contexto da anterior, a tela mostra
                qual pergunta foi de fato respondida — se o produto entendeu
                errado, dá para ver na hora.
              */}
              {f.resposta?.herdouContexto === true && (
                <p className="entendi">
                  Entendi como: <em>{f.resposta.perguntaResolvida}</em>
                </p>
              )}
              {formatar(f.texto)}

              {/*
                🔴 O plano aparece como FICHA, não como parágrafo.
                Quem vai autorizar uma escrita no parque precisa ver o alvo e a
                contagem separados do texto — número embutido em prosa é número
                que ninguém confere antes de clicar.
              */}
              {f.resposta?.plano !== undefined && (
                <FichaDoPlano
                  plano={f.resposta.plano}
                  executando={executando === i}
                  {...(f.perguntaOriginal === undefined
                    ? {}
                    : { aoConfirmar: () => void confirmar(i, f.perguntaOriginal ?? '') })}
                />
              )}

              {f.resposta?.dados !== undefined && f.resposta.dados.grupos.length > 0 && (
                <ul className="grupos-assistente">
                  {f.resposta.dados.grupos.slice(0, 6).map((g) => (
                    <li key={g.chave}>
                      <span>{g.chave}</span>
                      <b>{g.total}</b>
                    </li>
                  ))}
                </ul>
              )}

              {/*
                A fonte é obrigatória quando há resposta. É o que separa este
                assistente de um que inventa: dá para conferir de onde veio.
              */}
              {f.resposta !== undefined && f.resposta.fontes.length > 0 && (
                <p className="fontes">
                  Fonte: {f.resposta.fontes.map((x) => x.titulo).join(' · ')}
                </p>
              )}

              {f.resposta?.leva !== undefined && f.resposta.leva !== null && (
                <button
                  className="link"
                  onClick={() => {
                    irPara(f.resposta?.leva ?? 'inicio');
                    setAberto(false);
                  }}
                >
                  Ver na tela →
                </button>
              )}
            </div>
          </div>
        ))}

        {/*
          Continuação. Sem isto o painel parece um formulário de consulta: você
          pergunta, lê e não sabe que dá para seguir. Duas ou três sugestões
          bastam para deixar claro que é conversa.
        */}
        {!pensando && ultima?.resposta !== undefined && (
          <div className="seguir">
            {seguintes(ultima.resposta).map((s2) => (
              <button key={s2} onClick={() => void enviar(s2)}>
                {s2}
              </button>
            ))}
          </div>
        )}

        {pensando && (
          <div className="fala de-assistente">
            <div className="balao suave">Pensando…</div>
          </div>
        )}
        <div ref={fim} />
      </div>

      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void enviar(pergunta);
        }}
      >
        <input
          ref={campo}
          value={pergunta}
          placeholder="Escreva sua pergunta…"
          onChange={(e) => setPergunta(e.target.value)}
          maxLength={500}
          autoFocus
        />
        <button className="primario" type="submit" disabled={pensando}>
          Enviar
        </button>
      </form>
    </div>
  );
}

/**
 * Formatação mínima: negrito e quebra de linha.
 *
 * Sem biblioteca de markdown. O texto vem da nossa própria base de
 * conhecimento, então o conjunto de marcações é conhecido e pequeno — e
 * `dangerouslySetInnerHTML` num painel que também exibe dado do parque seria
 * abrir uma porta sem necessidade.
 */
function formatar(texto: string): ReactNode {
  return texto.split('\n').map((linha, i) => (
    <p key={i} className={linha === '' ? 'vazia' : undefined}>
      {linha
        .split(/(\*\*[^*]+\*\*)/g)
        .map((parte, j) =>
          parte.startsWith('**') && parte.endsWith('**') ? (
            <strong key={j}>{parte.slice(2, -2)}</strong>
          ) : (
            <span key={j}>{parte}</span>
          ),
        )}
    </p>
  ));
}

/**
 * O que oferecer depois de uma resposta.
 *
 * Sugestão depende do que ele acabou de responder: quem viu um número costuma
 * querer a lista, quem leu uma explicação costuma querer o número. As frases
 * são as que o roteamento sabe atender — sugerir o que ele não responde é pior
 * que não sugerir nada.
 */
function seguintes(r: Resposta): readonly string[] {
  if (r.tipo === 'nao_sei') {
    return ['Quantas máquinas ainda estão no Windows 10?', 'O que é o Sentinela?'];
  }
  if (r.tipo === 'dados') {
    return [
      'Quais máquinas estão sem agente?',
      'Máquinas sem reportar há mais de 30 dias',
      'O que é uma violação de conformidade?',
    ];
  }
  return [
    'Qual o software mais instalado no parque?',
    'Quantas violações estão abertas?',
    'Como funciona a remediação?',
  ];
}

/**
 * O que será feito, antes de alguém dizer sim.
 *
 * ⚠️ Não há botão de confirmar aqui ainda. Ele entra quando a rota de execução
 * existir — um botão que não executa é pior que a ausência dele: ensina a
 * clicar e some com a confiança na primeira vez que nada acontece.
 */
function FichaDoPlano({
  plano,
  aoConfirmar,
  executando,
}: {
  plano: Plano;
  aoConfirmar?: () => void;
  executando: boolean;
}): ReactNode {
  const ONDE_AUTORIZA: Record<Plano['autorizacao'], string> = {
    chat: 'Confirmação aqui mesmo',
    aprovacao: 'Fila de Aprovações',
    previa: 'Autorização prévia já concedida',
  };

  return (
    <div className="ficha-plano">
      <dl>
        <dt>O quê</dt>
        <dd>{plano.oQue}</dd>
        <dt>Onde</dt>
        <dd>
          {/* `null` é "não resolvido", e nunca deve ser lido como "todas". */}
          <strong>{plano.maquinas ?? '—'}</strong> máquina(s) · {plano.alvo}
        </dd>
        <dt>Autoriza</dt>
        <dd>{ONDE_AUTORIZA[plano.autorizacao]}</dd>
      </dl>
      {plano.ressalva !== null && <p className="ressalva">⚠️ {plano.ressalva}</p>}

      {/*
        🔴 O botão só aparece quando há o que confirmar aqui.

        Plano que vai para a fila de Aprovações não ganha botão: ele já está
        dito no texto, e um botão que "confirma" algo que outra pessoa vai
        aprovar ensina a clicar sem ler — na terceira vez ninguém lê mais.
      */}
      {aoConfirmar !== undefined && plano.autorizacao !== 'aprovacao' && (
        <button className="primario confirmar-plano" disabled={executando} onClick={aoConfirmar}>
          {executando ? 'Executando…' : `Confirmar em ${plano.maquinas ?? 0} máquina(s)`}
        </button>
      )}
    </div>
  );
}
